import { GET_PROMOTIONAL_BANNER } from "@membership/graphql";
import type { PromotionalBannerType } from "@shared/graphql.types";
import { getRegion } from "@shared/utils.locale";
import { branchName, preview } from "data/contentful/constants";
import {
  accessTokenHasExpired,
  createRequestContext,
  getClient
} from "lib/apolloClient";
import { useUser } from "lib/user";
import { useEffect, useState } from "react";
import { getUserSegment } from "utils/userSegment";
import type { IPromotionalBannerResponse } from "./types";
import { mapPromotionalBannerDetails } from "./utils";
import { refreshToken } from "lib/api";

const client = getClient();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function createCacheKey(userSegment: string, sku: string) {
  return `promoBanner:${userSegment}:${sku}`;
}

export function usePromotionalBanner(): IPromotionalBannerResponse {
  const userSegment = getUserSegment();
  const user = useUser();

  const [data, setData] = useState<{
    getPromotionalBanner: PromotionalBannerType;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    const cacheKey = createCacheKey(
      userSegment || "nonmember",
      user.membership?.id || `${getRegion()}`
    );

    async function fetchBanner() {
      const cachedRaw = localStorage.getItem(cacheKey);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        const age = Date.now() - cached.timestamp;
        if (age < CACHE_TTL) {
          setData(cached.data);
          setLoading(false);
          return; // use the cached data
        } else {
          localStorage.removeItem(cacheKey); // expired
        }
      }

      setLoading(true);

      if (
        userSegment === "member" &&
        user?.isLoggedIn &&
        accessTokenHasExpired()
      ) {
        await refreshToken().catch(() => "");
      }

      try {
        const result = await client.query({
          query: GET_PROMOTIONAL_BANNER,
          variables: { branchName, preview },
          fetchPolicy: "network-only",
          context: createRequestContext({ sessionStrategy: "NONE" })
        });

        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            timestamp: Date.now(),
            data: result.data
          })
        );

        setData(result.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    }

    fetchBanner();
  }, [userSegment, user.membership?.id, user.isLoggedIn]);

  return {
    loading,
    error,
    promotionalBannerDetails:
      !error && data?.getPromotionalBanner
        ? mapPromotionalBannerDetails(data.getPromotionalBanner)
        : null
  };
}
